import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowUpRight, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const usd0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const ca = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA`;

/**
 * "If I sold everything today" — every CA and BOT the account holds, wherever it
 * sits, at today's price. Sellable-today and still-locked are kept apart because
 * a fixed stake genuinely can't be sold this morning, and spent money (hashrate,
 * miners, USDT deposits) is named as excluded rather than quietly left out.
 */
export default function LiquidValueCard({ portfolioId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (refresh = 0) => {
    setBusy(!!refresh);
    api.get("/liquid-value", { params: { portfolio: portfolioId, refresh } })
      .then((r) => { setD(r.data); setErr(false); })
      .catch(() => setErr(true))
      .finally(() => setBusy(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setD(null); load(0); }, [portfolioId]);

  if (err || (!d && !busy)) return null;
  if (!d) return <div className="glass p-5 mb-5 text-sm text-ink-3" data-testid="liquid-card-loading">Adding up what everything is worth…</div>;

  const segs = [
    { k: "CA you can sell now", v: (d.ca.staking_free + d.ca.mining_claimable + d.ca.community_claimable) * d.ca_price, c: "#4060FF" },
    { k: "CA in the wallet", v: d.ca.wallet * d.ca_price, c: "#60A5FA" },
    { k: "BOT you can sell now", v: (d.bot.rewards_claimable + d.bot.wallet) * d.bot_price, c: "#0E7490" },
    { k: "CA locked in terms", v: d.ca.staking_locked * d.ca_price, c: "#CBD5E1" },
    { k: "BOT pledged", v: d.bot.pledged * d.bot_price, c: "#94A3B8" },
  ].filter((s) => s.v > 0);
  const span = segs.reduce((a, s) => a + s.v, 0) || 1;

  return (
    <div className="glass-strong p-5 mb-5" data-testid="liquid-value-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(0,134,58,0.12)" }}>
          <Wallet size={16} style={{ color: "#00863A" }} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold tracking-tight leading-tight">If you sold everything today</h3>
          <div className="kpi-label mt-0.5">
            {d.portfolio.wallet_count} wallet{d.portfolio.wallet_count === 1 ? "" : "s"} · CA ${Number(d.ca_price).toFixed(2)} · BOT ${Number(d.bot_price).toFixed(2)}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => load(1)} disabled={busy} title="Re-read every wallet's balance from the chain"
            className="text-ink-3 hover:text-ink transition-colors disabled:opacity-40" data-testid="liquid-card-refresh">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
          </button>
          <Link to="/liquid-value" className="pill text-xs inline-flex items-center gap-1" data-testid="liquid-card-link">
            Wallet by wallet <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-x-10 gap-y-3 mt-4">
        <div>
          <div className="kpi-label">Sellable right now</div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight tabular-nums" style={{ color: "#00863A" }} data-testid="liquid-card-sellable">
            {usd0(d.sellable_usd)}
          </div>
        </div>
        <div>
          <div className="kpi-label">Once the terms unlock</div>
          <div className="font-display text-2xl md:text-3xl font-bold tracking-tight tabular-nums text-ink-2" data-testid="liquid-card-total">
            {usd0(d.total_usd)}
          </div>
        </div>
        <div className="text-xs text-ink-3 leading-snug">
          <div data-testid="liquid-card-ca">{ca(d.ca.total)} across staking, mining and the wallets</div>
          <div data-testid="liquid-card-bot">{Number(d.bot.total || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} BOT pledged, claimable and held</div>
        </div>
      </div>

      <div className="flex h-2.5 rounded-full overflow-hidden mt-4" data-testid="liquid-card-bar">
        {segs.map((s) => (
          <div key={s.k} title={`${s.k} · ${usd0(s.v)}`} style={{ width: `${(s.v / span) * 100}%`, background: s.c }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {segs.map((s) => (
          <span key={s.k} className="text-[10px] text-ink-3 inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: s.c }} />{s.k} {usd0(s.v)}
          </span>
        ))}
      </div>

      <p className="text-[11px] text-ink-3 leading-snug mt-3 pt-3 border-t" style={{ borderColor: "rgba(15,18,38,0.07)" }} data-testid="liquid-card-excluded">
        Not in these numbers: {usd0(d.excluded.hashrate_usd)} of hashrate{d.excluded.miner_usd > 0 ? ` and ${usd0(d.excluded.miner_usd)} of miners` : ""} —
        that money bought the income and can&apos;t be sold back.
        {d.bot_unread > 0 ? ` ${d.bot_unread} wallet${d.bot_unread === 1 ? "'s" : "s'"} BOT mining account hasn't been read yet, so any claimable BOT there is missing.` : ""}
      </p>
    </div>
  );
}
